import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi } from "@/api/admin.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { errorMessage } from "./first-round";
import { FirstRoundPlans } from "./FirstRoundPlans";
import { useAdminProduct } from "./hooks";
import { type Column, DataTable } from "./ui/DataTable";
import { PageHeader } from "./ui/PageHeader";

export function PlansManagement() {
  const { product } = useAdminProduct();
  // theResume sells First Round plans (Free + one-off passes), edited on their own page.
  if (product === "THERESUME") return <FirstRoundPlans />;
  return <RecruitPlans product={product} />;
}

const isPrice = (value: string) => /^\d+(\.\d{1,2})?$/.test(value.trim());

function RecruitPlans({ product }: { product: "THERECRUIT" }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: plans, isLoading, error } = useQuery({ queryKey: ["plans", product], queryFn: () => adminApi.listPlans(product) });
  const mutate = useMutation({
    mutationFn: (payload: { id: string; monthlyPrice?: number; annualPrice?: number }) =>
      adminApi.updatePlan(payload.id, { monthlyPrice: payload.monthlyPrice, annualPrice: payload.annualPrice }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plans", product] });
      toast({ title: "Plan prices saved", variant: "success" });
    },
    onError: (e) => toast({ title: "Couldn't save the plan", description: errorMessage(e), variant: "destructive" }),
  });

  const columns: Column<any>[] = [
    { key: "name", header: "Plan", cell: (p) => <span className="font-medium">{p.displayName}</span> },
    { key: "tier", header: "Tier", cell: (p) => <code className="font-mono text-xs text-slate-600">{p.tier}</code> },
    {
      key: "prices",
      header: "Monthly · Annual (USD)",
      cell: (p) => <PriceEditor plan={p} saving={mutate.isPending} onSave={(data) => mutate.mutate({ id: p.id, ...data })} />,
    },
  ];

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Plans"
        description="theRecruit subscription prices. Saving changes the price shown in the app; check that the Stripe price matches before saving."
      />
      <DataTable label="Plans" columns={columns} rows={plans} rowKey={(p) => p.id} isLoading={isLoading} error={error} pageSize={null} />
    </div>
  );
}

function PriceEditor({
  plan,
  saving,
  onSave,
}: {
  plan: any;
  saving: boolean;
  onSave: (data: { monthlyPrice: number; annualPrice: number }) => void;
}) {
  const [m, setM] = useState<string>(String(plan.monthlyPrice ?? 0));
  const [a, setA] = useState<string>(String(plan.annualPrice ?? 0));
  const dirty = Number(m) !== Number(plan.monthlyPrice ?? 0) || Number(a) !== Number(plan.annualPrice ?? 0);
  const valid = isPrice(m) && isPrice(a);
  return (
    <div className="flex items-center gap-2">
      <Input className="h-8 w-24" inputMode="decimal" aria-label={`${plan.displayName} monthly price`} value={m} onChange={(e) => setM(e.target.value)} />
      <Input className="h-8 w-24" inputMode="decimal" aria-label={`${plan.displayName} annual price`} value={a} onChange={(e) => setA(e.target.value)} />
      <Button size="sm" variant="outline" disabled={!dirty || !valid || saving} onClick={() => onSave({ monthlyPrice: Number(m), annualPrice: Number(a) })}>
        Save
      </Button>
      {!valid && <span className="text-xs text-red-600">Use a price like 49 or 49.99</span>}
    </div>
  );
}
